import React, { useState } from 'react';
import { useParams, Link } from '@tanstack/react-router';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ArrowSquareOutIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import {
  getIntegrationConfig,
  INTEGRATION_CATEGORIES,
} from 'src/config/integrations';
import {
  useListIntegrations,
  useUpdateIntegration,
} from 'src/api/rpc/hooks/integrations/useIntegrations';
import { IntegrationAvailability, IntegrationKey } from '@erp/rpc';
import { useToast } from 'src/context/ToastContext';
import paths from 'src/paths';

// Import specific settings components
import { FamousIntegrationSettings } from './settings/FamousIntegrationSettings';
import { PlaidIntegrationSettings } from './settings/PlaidIntegrationSettings';
import { PaymentsIntegrationSettings } from './settings/PaymentsIntegrationSettings';

// Placeholder for internal integrations that don't need settings UI
const NoSettingsComponent = () => null;

// Mapping of integration keys to their settings components
const INTEGRATION_SETTINGS_COMPONENTS: Record<
  IntegrationKey,
  React.ComponentType<{ availability?: IntegrationAvailability }>
> = {
  [IntegrationKey.Famous]: FamousIntegrationSettings,
  [IntegrationKey.Plaid]: PlaidIntegrationSettings,
  [IntegrationKey.Payments]: PaymentsIntegrationSettings,
};

export function IntegrationDetailPage() {
  const params = useParams({
    from: '/authenticated/integrations/$integrationKey',
  });
  const integrationKey = params.integrationKey;
  const { data: integrationsData, isLoading, refetch } = useListIntegrations();
  const installedIntegrations = integrationsData?.installed ?? {};
  const availability = integrationsData?.availability ?? {};
  const integrationAvailability =
    availability[integrationKey as IntegrationKey] ?? undefined;
  const updateIntegration = useUpdateIntegration();
  const { showToast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);

  const integrationConfig = getIntegrationConfig(integrationKey);
  const isInstalled = Boolean(
    installedIntegrations && installedIntegrations[integrationKey],
  );
  const isAvailable = integrationAvailability?.available ?? true;
  const unavailableReason = integrationAvailability?.reason;
  const categoryInfo = INTEGRATION_CATEGORIES[integrationConfig.category];
  const IconComponent = integrationConfig.icon;
  const SettingsComponent = INTEGRATION_SETTINGS_COMPONENTS[integrationKey];

  const handleConnect = async () => {
    if (!integrationConfig.defaultSettings) {
      showToast({
        title: 'Configuration Required',
        message: 'This integration requires manual configuration.',
        color: 'warning',
        icon: 'warning',
      });
      return;
    }

    if (!isAvailable) {
      showToast({
        title: 'Integration Unavailable',
        message:
          unavailableReason ||
          `${integrationConfig.name} cannot be configured right now.`,
        color: 'warning',
        icon: 'warning',
      });
      return;
    }

    setIsConnecting(true);
    try {
      await updateIntegration.mutateAsync({
        key: integrationKey,
        settings: integrationConfig.defaultSettings,
      } as Parameters<typeof updateIntegration.mutateAsync>[0]);

      // Refetch the integrations list to update the UI
      await refetch();

      showToast({
        title: 'Integration Connected',
        message: `${integrationConfig.name} has been successfully connected with default settings.`,
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: 'Connection Failed',
        message: `Failed to connect ${integrationConfig.name}. Please try again.`,
        color: 'error',
        icon: 'error',
      });
    } finally {
      setIsConnecting(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header with Back Button */}
      <div className="mb-8">
        <Link to={paths.authenticated.integrations.list}>
          <Button variant="ghost" size="sm" className="mb-4">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Integrations
          </Button>
        </Link>

        <div className="flex items-start gap-4">
          <div className="p-3 bg-gray-100 rounded-lg">
            <IconComponent className="h-8 w-8 text-gray-700" />
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold">{integrationConfig.name}</h1>
              {isInstalled && (
                <Badge
                  variant="outline"
                  className="bg-green-100 text-green-700 border-green-300"
                >
                  <CheckCircleIcon className="h-4 w-4 mr-1" />
                  Installed
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2 mb-3">
              <Badge variant="secondary">{categoryInfo.name}</Badge>
              {integrationConfig.isPopular && (
                <Badge variant="outline">Popular</Badge>
              )}
              {!isAvailable && (
                <Badge
                  variant="outline"
                  className="border-red-300 text-red-700 bg-red-50"
                >
                  Unavailable
                </Badge>
              )}
            </div>

            <p className="text-lg text-gray-600 mb-4">
              {integrationConfig.longDescription}
            </p>
            {!isAvailable && (
              <p className="text-sm text-red-600 mb-2">
                {unavailableReason ||
                  `${integrationConfig.name} cannot be configured right now.`}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Integration Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Features & Info */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Features</CardTitle>
              <CardDescription>What this integration provides</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {integrationConfig.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircleIcon className="h-4 w-4 text-green-600 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Additional info card could go here */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg">Support</CardTitle>
              <CardDescription>
                Need help with this integration?
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* <Button variant="outline" size="sm" className="w-full">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Documentation
                </Button> */}
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <a
                    href="mailto:support@gently-ai.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ArrowSquareOutIcon className="h-4 w-4 mr-2" />
                    Contact Support
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Settings */}
        <div className="lg:col-span-2">
          {isInstalled ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Settings</CardTitle>
                <CardDescription>
                  Configure your {integrationConfig.name} integration
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!isAvailable && (
                  <p className="text-sm text-red-600 mb-4">
                    {unavailableReason ||
                      `${integrationConfig.name} is currently unavailable.`}
                  </p>
                )}
                {SettingsComponent && (
                  <SettingsComponent availability={integrationAvailability} />
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Connect {integrationConfig.name}
                </CardTitle>
                <CardDescription>
                  Start using {integrationConfig.name} with your account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <IconComponent className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">
                    Ready to connect?
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Connect {integrationConfig.name} to start syncing your data
                    and streamlining your workflow.
                  </p>
                  {!isAvailable && (
                    <p className="text-sm text-red-600 mb-4">
                      {unavailableReason ||
                        `${integrationConfig.name} is currently unavailable.`}
                    </p>
                  )}
                  <Button
                    size="lg"
                    className="px-8"
                    onClick={handleConnect}
                    disabled={isConnecting || !isAvailable}
                  >
                    {isConnecting ? (
                      <>
                        <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                        Connecting...
                      </>
                    ) : isAvailable ? (
                      `Connect ${integrationConfig.name}`
                    ) : (
                      'Unavailable'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
